import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  Phone,
  User,
  Mail,
  MapPin,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
  MailCheck,
  Layers,
  Sparkles,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import AuthSplitLayout from "../../components/auth/AuthSplitLayout";
import NetworkSelector from "../../components/auth/NetworkSelector";
import { isValidMobileNetwork } from "../../constants/mobileNetworks";
import { registerStart, registerSuccess, registerFailure, resetAuthFlowState } from "../../store/slices/userSlice";
import { registerConsumer, getErrorMessage } from "../../api/userAuthApi";
import { normalizePhoneForApi } from "../../utils/phoneNormalize";
import showToast  from "../../utils/toast";
import { clearApplyIntent, readApplyIntent } from "../../utils/applyIntent";
import { useCategories } from "../../contexts/CategoriesContext";

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#FFD221] focus:ring-4 focus:ring-[#FFD221]/20";

/**
 * Normalized map: category display name → unique sub-category names (matches picker / dropdown labels).
 */
function buildCategoriesSubcategoriesMap(interestByCategory) {
  const out = {};
  if (!interestByCategory || typeof interestByCategory !== "object") return out;
  for (const [category, subs] of Object.entries(interestByCategory)) {
    const c = String(category || "").trim();
    if (!c) continue;
    const uniq = [...new Set((Array.isArray(subs) ? subs : []).map((s) => String(s || "").trim()).filter(Boolean))];
    if (uniq.length) out[c] = uniq;
  }
  return out;
}

/** Plain object for JSON body: same shape as `interests` (defensive copy). */
function cloneInterestsPayload(map) {
  return Object.fromEntries(
    Object.entries(map || {}).map(([category, subs]) => [
      category,
      Array.isArray(subs) ? [...subs] : [],
    ]),
  );
}

/** Single category → sub-category map from article context (modal embed). */
function mapFromEmbedArticleInterests(ai) {
  const c = String(ai?.category || "").trim();
  const s = String(ai?.subCategory || "").trim();
  if (!c || !s) return {};
  return { [c]: [s] };
}

/** Full-height right drawer for single category + sub-topic selection (portaled to document.body). */
function SignupInterestsDrawer({
  open,
  onClose,
  embed,
  catsLoading,
  categories,
  expandedByCategory,
  toggleCategoryAccordion,
  expandAllCategoryPanels,
  collapseAllCategoryPanels,
  interestByCategory,
  toggleSubCategory,
  categoriesPayload,
  selectedSubCount,
  submitting,
}) {
  const panelTitleId = "signup-interests-drawer-title";

  return (
    <AnimatePresence mode="sync">
      {open ? (
        <motion.div
          key="signup-interests-shell"
          role="presentation"
          className="fixed inset-0 z-[260] flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            aria-label="Close topic browser"
            className="absolute inset-0 bg-slate-950/[0.45] backdrop-blur-[3px]"
            onClick={onClose}
          />

          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-labelledby={panelTitleId}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 340 }}
            className={[
              "relative z-10 flex h-[100dvh] w-full max-w-[min(100vw,22rem)] flex-col bg-white shadow-[-12px_0_48px_-12px_rgba(15,23,42,0.28)] sm:max-w-md lg:max-w-[26.5rem]",
              embed ? "rounded-l-2xl" : "rounded-l-2xl sm:rounded-l-3xl",
            ].join(" ")}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FFD221] via-amber-300 to-[#FFD221]" />
            <div className="shrink-0 border-b border-slate-200/90 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] text-white sm:px-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FFD221]/90">
                    Personalise
                  </p>
                  <h2 id={panelTitleId} className="mt-1 text-lg font-black leading-tight tracking-tight sm:text-xl">
                    Your content interests
                  </h2>
                  <p className="mt-1.5 text-xs leading-relaxed text-white/65 sm:text-[13px]">
                    Choose one category and one sub-topic to start. You can add more interests from your profile after signing in.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white transition hover:bg-white/18"
                  aria-label="Close"
                >
                  <X size={20} strokeWidth={2.25} />
                </button>
              </div>
              <div
                className={[
                  "mt-4 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left",
                  selectedSubCount > 0
                    ? "border-emerald-400/30 bg-emerald-500/15 text-emerald-50"
                    : "border-amber-400/25 bg-amber-500/10 text-amber-50/95",
                ].join(" ")}
              >
                {selectedSubCount > 0 ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" strokeWidth={2.25} aria-hidden />
                ) : (
                  <AlertCircle className="h-5 w-5 shrink-0 text-amber-300" strokeWidth={2.25} aria-hidden />
                )}
                <p className="min-w-0 text-[11px] font-semibold leading-snug sm:text-xs">
                  {selectedSubCount > 0 ? (
                    <>
                      <span className="text-white">1 topic selected</span>
                      <span className="font-normal text-white/70"> · 1 area</span>
                    </>
                  ) : (
                    <>Pick one category and one sub-topic to finish sign-up.</>
                  )}
                </p>
              </div>
            </div>

            <div className="shrink-0 border-b border-slate-100 bg-slate-50/90 px-4 py-2.5 sm:px-5">
              {!catsLoading && categories.some((c) => (c.sub_categories ?? []).length > 0) ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <Sparkles className="h-3.5 w-3.5 text-[#9a7b00]" strokeWidth={2.5} aria-hidden />
                    Quick actions
                  </span>
                  <div className="flex items-center gap-2 text-[11px] font-semibold sm:text-xs">
                    <button type="button" onClick={expandAllCategoryPanels} className="text-[#9a7b00] hover:underline">
                      Expand all
                    </button>
                    <span className="text-slate-300">·</span>
                    <button
                      type="button"
                      onClick={collapseAllCategoryPanels}
                      className="text-slate-500 hover:text-slate-800"
                    >
                      Collapse all
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {catsLoading ? (
                <div className="flex flex-col items-center gap-3 py-16 text-slate-400">
                  <Loader2 className="h-8 w-8 animate-spin text-[#9a7b00]" />
                  <p className="text-sm font-medium">Loading topic areas…</p>
                </div>
              ) : !categories.length ? (
                <div className="px-6 py-16 text-center text-sm text-slate-500">No categories available.</div>
              ) : (
                <div className="divide-y divide-slate-100 px-3 py-3 sm:px-4">
                  {categories.map((cat, catIndex) => {
                    const subs = cat.sub_categories ?? [];
                    const expanded = Boolean(expandedByCategory[cat.name]);
                    const nSelected = interestByCategory[cat.name]?.length ?? 0;
                    const panelId = `signup-drawer-acc-${catIndex}`;
                    const headerId = `signup-drawer-hdr-${catIndex}`;
                    const canOpen = subs.length > 0 && !submitting;
                    return (
                      <section key={cat.name} className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
                        <button
                          type="button"
                          id={headerId}
                          disabled={!canOpen}
                          onClick={() => canOpen && toggleCategoryAccordion(cat.name)}
                          className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition sm:px-3.5 sm:py-3 ${
                            canOpen ? "hover:bg-slate-50/95" : "cursor-not-allowed opacity-50"
                          }`}
                          aria-expanded={expanded}
                          aria-controls={panelId}
                        >
                          <ChevronDown
                            size={18}
                            strokeWidth={2.25}
                            className={`shrink-0 text-slate-400 transition-transform duration-200 ${
                              expanded ? "rotate-180" : ""
                            }`}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1 text-sm font-bold text-slate-900">{cat.name}</span>
                          {nSelected > 0 ? (
                            <span className="shrink-0 rounded-full bg-[#FFD221] px-2 py-0.5 text-[11px] font-black text-black">
                              {nSelected}
                            </span>
                          ) : null}
                        </button>
                        {expanded && subs.length > 0 ? (
                          <div
                            id={panelId}
                            role="group"
                            aria-labelledby={headerId}
                            className="border-t border-slate-100 bg-slate-50/70 px-2 py-2 sm:px-3"
                          >
                            <ul className="space-y-1">
                              {subs.map((s) => {
                                const picked = (interestByCategory[cat.name] || []).includes(s.name);
                                return (
                                  <li key={`${cat.name}-${s.name}`}>
                                    <label
                                      className={`flex cursor-pointer items-start gap-3 rounded-xl px-2 py-2 transition ${
                                        picked
                                          ? "bg-[#FFD221]/12 ring-1 ring-[#FFD221]/35"
                                          : "hover:bg-white/80"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name="signup-interest-sub"
                                        className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 accent-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/30"
                                        checked={picked}
                                        onChange={() => toggleSubCategory(cat.name, s.name)}
                                        disabled={submitting}
                                      />
                                      <span
                                        className={`min-w-0 text-sm leading-snug ${picked ? "font-semibold text-slate-900" : "text-slate-700"}`}
                                      >
                                        {s.name}
                                      </span>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                      </section>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-slate-200/90 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-12px_32px_-16px_rgba(15,23,42,0.12)] sm:px-5 sm:py-4">
              <motion.button
                type="button"
                onClick={onClose}
                whileTap={{ scale: 0.985 }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-black text-white shadow-lg shadow-slate-900/25 transition hover:bg-slate-800"
              >
                Save &amp; return to form
              </motion.button>
              <p className="mt-2 text-center text-[10px] text-slate-400 sm:text-[11px]">
                Selection syncs instantly with the sign-up form behind this panel.
              </p>
            </div>
          </motion.aside>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

// ─── Post–sign-up panel (no forced countdown — user chooses when to sign in) ──
const SuccessModal = ({ isOpen, onClose, email, postAuthRedirect, embed, onContinueToSignIn }) => {
  const navigate = useNavigate();

  const goSignIn = () => {
    onClose();
    const clean = String(email || "").trim();
    if (embed && onContinueToSignIn) {
      onContinueToSignIn(clean);
      return;
    }
    navigate("/signin", {
      replace: true,
      state: {
        ...(clean ? { prefilledEmail: clean } : {}),
        fromRegistration: true,
        ...(postAuthRedirect ? { postAuthRedirect } : {}),
      },
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/45 backdrop-blur-[2px]"
            aria-hidden
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", damping: 26, stiffness: 320 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signup-success-title"
            className="relative w-full max-w-md overflow-hidden rounded-[1.35rem] border border-slate-200/80 bg-white shadow-[0_24px_64px_-12px_rgba(0,0,0,0.22)]"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FFD221] via-amber-400 to-[#FFD221]" />

            <div className="px-6 pb-7 pt-8 sm:px-8">
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFD221]/15 ring-1 ring-[#FFD221]/35">
                <MailCheck className="h-7 w-7 text-[#9a7b00]" strokeWidth={2} aria-hidden />
              </div>

              <h3
                id="signup-success-title"
                className="text-center text-xl font-black tracking-tight text-slate-900 sm:text-2xl"
              >
                Account created
              </h3>
              <p className="mt-3 text-center text-sm leading-relaxed text-slate-600">
                Your next step is in your inbox. We&apos;ve sent your{" "}
                <span className="font-semibold text-slate-800">login details</span> to:
              </p>
              <p className="mt-3 break-all rounded-xl border border-slate-200/90 bg-slate-50 px-3 py-2.5 text-center text-sm font-semibold text-slate-900">
                {email}
              </p>
              <p className="mt-3 text-center text-xs leading-relaxed text-slate-500">
                Check spam or promotions if you don&apos;t see it within a few minutes. Use those
                credentials on the next screen to sign in.
              </p>

              <div className="mt-8 flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={goSignIn}
                  className="w-full rounded-xl bg-[#FFD221] px-4 py-3.5 text-sm font-black text-black shadow-md transition hover:brightness-95 active:scale-[0.99]"
                >
                  Continue to sign in
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    if (!embed) {
                      navigate("/", { replace: true });
                    }
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  {embed ? "I'll finish later" : "Back to home"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// ────────────────────────────────────────────────────────────────────────────

const SignUp = (props = {}) => {
  const {
    embed = false,
    onRequestSignIn,
    onEmbedClose,
    postAuthRedirect: embedPostAuthRedirect,
    embedArticleInterests,
  } = props;

  const embedHasPresetInterests = Boolean(
    embed &&
      embedArticleInterests &&
      String(embedArticleInterests.category || "").trim() &&
      String(embedArticleInterests.subCategory || "").trim(),
  );
  const embedPresetMap = embedHasPresetInterests
    ? mapFromEmbedArticleInterests(embedArticleInterests)
    : null;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationStatus, registrationError, pendingVerification } = useSelector(
    (s) => s.infoboxx_consumer
  );

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    location: "",
  });
  const [network, setNetwork] = useState("");
  const [showNetworkRequiredHint, setShowNetworkRequiredHint] = useState(false);

  /** Record<categoryName, subCategoryName[]> — matches API-facing structure */
  const [interestByCategory, setInterestByCategory] = useState(() =>
    embedPresetMap ? { ...embedPresetMap } : {},
  );
  /** Which category rows are expanded in the accordion */
  const [expandedByCategory, setExpandedByCategory] = useState({});
  const accordionInitializedRef = useRef(false);
  /** Full topic list in a portaled right drawer */
  const [topicsPickerOpen, setTopicsPickerOpen] = useState(false);
  /** Inline validation: user tried to submit without interests */
  const [showTopicsRequiredHint, setShowTopicsRequiredHint] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const { signupCategories: categories, loading: catsLoading } = useCategories();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const fieldInputClass = embed
    ? "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/20"
    : inputClassName;
  const fieldLabelClass = embed
    ? "text-[11px] font-semibold tracking-wide uppercase text-slate-500"
    : "text-xs font-semibold tracking-wide uppercase text-slate-500";
  const fieldGapClass = embed ? "space-y-1" : "space-y-1.5";
  const iconSize = embed ? 15 : 17;

  useEffect(() => {
    if (embed) return;
    window.scrollTo(0, 0);
  }, [embed]);

  useEffect(() => {
    if (!embed) return;
    if (embedHasPresetInterests) {
      setInterestByCategory(mapFromEmbedArticleInterests(embedArticleInterests));
    } else {
      setInterestByCategory({});
    }
    setShowTopicsRequiredHint(false);
    setTopicsPickerOpen(false);
  }, [embed, embedHasPresetInterests, embedArticleInterests]);

  // Open the first category that has sub-topics once, so users see what to do.
  useEffect(() => {
    if (embedHasPresetInterests) {
      accordionInitializedRef.current = true;
      return;
    }
    if (accordionInitializedRef.current || catsLoading || !categories.length) return;
    const firstWithSubs = categories.find((c) => (c.sub_categories ?? []).length > 0);
    if (firstWithSubs) {
      setExpandedByCategory((prev) =>
        Object.keys(prev).length ? prev : { [firstWithSubs.name]: true },
      );
    }
    accordionInitializedRef.current = true;
  }, [categories, catsLoading, embedHasPresetInterests]);

  const toggleSubCategory = (categoryName, subName) => {
    const cat = String(categoryName || "").trim();
    const sub = String(subName || "").trim();
    if (!cat || !sub) return;
    setInterestByCategory((prev) => {
      const currentCat = Object.keys(prev)[0];
      const currentSub = prev[currentCat]?.[0];
      if (currentCat === cat && currentSub === sub) return {};
      return { [cat]: [sub] };
    });
  };

  const toggleCategoryAccordion = (categoryName) => {
    const key = String(categoryName || "").trim();
    if (!key) return;
    setExpandedByCategory((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAllCategoryPanels = () => {
    const next = {};
    categories.forEach((c) => {
      const name = c?.name;
      if (name && (c.sub_categories ?? []).length > 0) next[name] = true;
    });
    setExpandedByCategory(next);
  };

  const collapseAllCategoryPanels = () => setExpandedByCategory({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "phone_number") {
      const cleaned = value.replace(/[^\d\s\-()]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    dispatch(resetAuthFlowState());
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      location: "",
    });
    setNetwork("");
    setShowNetworkRequiredHint(false);
    setInterestByCategory(embedPresetMap ? { ...embedPresetMap } : {});
    setExpandedByCategory({});
    setTopicsPickerOpen(false);
    setShowTopicsRequiredHint(false);
    accordionInitializedRef.current = false;
    setTermsAccepted(false);
    clearApplyIntent();
    if (embed) {
      onEmbedClose?.();
      return;
    }
    navigate("/", { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!termsAccepted) {
      showToast.warning("Please accept the terms to continue.");
      return;
    }
    const msisdn = normalizePhoneForApi(formData.phone_number);
    if (!msisdn || msisdn.length < 9) {
      showToast.warning("Enter a valid phone number (local or with country code).");
      return;
    }
    if (!isValidMobileNetwork(network)) {
      setShowNetworkRequiredHint(true);
      showToast.warning("Select your mobile network (AT, Telecel, or MTN).");
      return;
    }
    const displayCategoriesMap = buildCategoriesSubcategoriesMap(
      embedPresetMap ?? interestByCategory,
    );
    const interestAreaCount = Object.keys(displayCategoriesMap).length;
    const interestTopicCount = Object.values(displayCategoriesMap).reduce(
      (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
      0,
    );
    if (interestAreaCount !== 1 || interestTopicCount !== 1) {
      setShowTopicsRequiredHint(true);
      setTopicsPickerOpen(true);
      showToast.warning("Select exactly one category and one sub-topic.");
      return;
    }
    try {
      // Keep the flow explicit: API call first, then reducer state update.
      dispatch(registerStart());
      const interests = cloneInterestsPayload(displayCategoriesMap);
      const payload = await registerConsumer({
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        msisdn,
        location: formData.location.trim(),
        network,
        interests,
        subscription_consent: termsAccepted ? "yes" : "no",
      });
      dispatch(registerSuccess(payload));

      setRegisteredEmail(formData.email.trim());
      setShowSuccessModal(true);
    } catch (err) {
      const message = getErrorMessage(err);
      dispatch(registerFailure(message));
      showToast.error(typeof message === "string" ? message : "Registration failed");
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const submitting = registrationStatus === "loading";

  const categoriesPayload = buildCategoriesSubcategoriesMap(
    embedPresetMap ?? interestByCategory,
  );
  const selectedSubCount = Object.values(categoriesPayload).reduce((n, arr) => n + arr.length, 0);

  useEffect(() => {
    if (selectedSubCount > 0) setShowTopicsRequiredHint(false);
  }, [selectedSubCount]);

  useEffect(() => {
    if (!topicsPickerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [topicsPickerOpen]);

  useEffect(() => {
    if (!topicsPickerOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setTopicsPickerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [topicsPickerOpen]);

  /** When the drawer opens, ensure at least one area is expanded for discoverability */
  useEffect(() => {
    if (!topicsPickerOpen || catsLoading || !categories.length) return;
    const first = categories.find((c) => (c.sub_categories ?? []).length > 0);
    if (!first) return;
    setExpandedByCategory((prev) => ({ ...prev, [first.name]: true }));
  }, [topicsPickerOpen, catsLoading, categories]);

  const signupFormBlock = (
        <div className={embed ? "space-y-2" : "space-y-4"}>
          {pendingVerification?.email ? (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className={
                embed
                  ? "flex items-start gap-2 rounded-xl border border-[#FFD221]/30 bg-[#FFD221]/8 px-3 py-2 text-xs text-slate-700"
                  : "flex items-start gap-3 rounded-2xl border border-[#FFD221]/30 bg-[#FFD221]/8 px-4 py-3 text-sm text-slate-700"
              }
            >
              <p className="min-w-0 flex-1">
                You have a verification in progress for{" "}
                <span className="font-semibold text-slate-900">{pendingVerification.email}</span>.{" "}
                <Link
                  to="/signin"
                  state={{
                    postAuthRedirect:
                      embedPostAuthRedirect ||
                      location.state?.postAuthRedirect ||
                      readApplyIntent() ||
                      undefined,
                  }}
                  className="font-semibold text-black underline-offset-2 hover:underline"
                >
                  Continue to verification
                </Link>
              </p>
            </motion.div>
          ) : null}

          {!embed ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                <X size={14} />
                Cancel
              </button>
            </div>
          ) : null}

          <form
            className={
              embed
                ? "grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-1.5"
                : "space-y-2"
            }
            onSubmit={handleSubmit}
          >
            <div
              className={
                embed
                  ? "col-span-2 grid grid-cols-2 gap-2"
                  : "grid grid-cols-1 gap-4 sm:grid-cols-2"
              }
            >
              <label className={fieldGapClass}>
                <span className={fieldLabelClass}>First name</span>
                <div className="relative">
                  <User
                    className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${embed ? "left-2.5" : "left-3"}`}
                    size={iconSize}
                  />
                  <input
                    className={`${embed ? "pl-9" : "pl-10"} ${fieldInputClass}`}
                    type="text"
                    name="first_name"
                    placeholder="Kwame"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={submitting}
                    required
                  />
                </div>
              </label>
              <label className={fieldGapClass}>
                <span className={fieldLabelClass}>Last name</span>
                <div className="relative">
                  <User
                    className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${embed ? "left-2.5" : "left-3"}`}
                    size={iconSize}
                  />
                  <input
                    className={`${embed ? "pl-9" : "pl-10"} ${fieldInputClass}`}
                    type="text"
                    name="last_name"
                    placeholder="Mensah"
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={submitting}
                    required
                  />
                </div>
              </label>
            </div>

            <label
              className={`block ${fieldGapClass} ${embed ? "sm:col-span-1" : "pt-2"}`}
            >
              <span className={fieldLabelClass}>Email address</span>
              <div className="relative">
                <Mail
                  className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${embed ? "left-2.5" : "left-3"}`}
                  size={iconSize}
                />
                <input
                  className={`${embed ? "pl-9" : "pl-10"} ${fieldInputClass}`}
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={submitting}
                  required
                />
              </div>
            </label>

            <label
              className={`block ${fieldGapClass} ${embed ? "sm:col-span-1" : "pt-2"}`}
            >
              <span className={fieldLabelClass}>Phone number</span>
              <div className="relative">
                <Phone
                  className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${embed ? "left-2.5" : "left-3"}`}
                  size={iconSize}
                />
                <input
                  className={`${embed ? "pl-9" : "pl-10"} ${fieldInputClass}`}
                  type="tel"
                  name="phone_number"
                  inputMode="numeric"
                  placeholder={embed ? "024… or 233…" : "e.g. 0241234567 or 233241234567"}
                  value={formData.phone_number}
                  onChange={handleChange}
                  disabled={submitting}
                  required
                />
              </div>
            </label>

            <div className={`${embed ? "col-span-2" : "pt-2"} space-y-1`}>
              <NetworkSelector
                value={network}
                onChange={(value) => {
                  setNetwork(value);
                  setShowNetworkRequiredHint(false);
                }}
                disabled={submitting}
                embed={embed}
                labelClassName={fieldLabelClass}
                error={showNetworkRequiredHint}
              />
              {showNetworkRequiredHint ? (
                <p className="text-[11px] font-semibold text-amber-700">
                  Please choose the network linked to your phone number.
                </p>
              ) : null}
            </div>

            <label className={`block ${fieldGapClass} ${embed ? "sm:col-span-2" : "pt-2"}`}>
              <span className={fieldLabelClass}>Location</span>
              <div className="relative">
                <MapPin
                  className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${embed ? "left-2.5" : "left-3"}`}
                  size={iconSize}
                />
                <input
                  className={`${embed ? "pl-9" : "pl-10"} ${fieldInputClass}`}
                  type="text"
                  name="location"
                  placeholder="e.g. Accra, Ghana"
                  value={formData.location}
                  onChange={handleChange}
                  disabled={submitting}
                  required
                />
              </div>
            </label>

            <div className={embed ? "col-span-2 space-y-2" : "col-span-2 space-y-2.5 pt-2"}>
                <div
                  className={[
                    "rounded-2xl border p-3 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-[box-shadow,border-color] sm:p-4",
                    showTopicsRequiredHint && selectedSubCount === 0
                      ? "border-amber-300/90 bg-gradient-to-br from-amber-50/90 to-white ring-2 ring-amber-200/60"
                      : selectedSubCount > 0
                        ? "border-emerald-200/70 bg-gradient-to-br from-emerald-50/40 via-white to-slate-50/30 ring-1 ring-emerald-100/80"
                        : "border-slate-200/80 bg-gradient-to-b from-white to-slate-50/40",
                  ].join(" ")}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={[
                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-inner",
                        selectedSubCount > 0
                          ? "bg-gradient-to-br from-emerald-600 to-emerald-800 text-white"
                          : "bg-gradient-to-br from-slate-800 to-slate-950 text-[#FFD221]",
                      ].join(" ")}
                    >
                      <Layers className="h-5 w-5" strokeWidth={2} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-[11px] font-bold tracking-wide text-slate-800 sm:text-xs">
                              Content interests
                            </p>
                            <span
                              className={[
                                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                                selectedSubCount > 0
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-amber-100 text-amber-900",
                              ].join(" ")}
                            >
                              {selectedSubCount > 0 ? "Complete" : "Required"}
                            </span>
                          </div>
                          <p
                            className={`mt-0.5 text-slate-500 ${embed ? "text-[10px] sm:text-xs" : "text-xs"}`}
                          >
                            {embedHasPresetInterests
                              ? "Pre-filled from this page — change it if you need a different topic."
                              : "Opens a side panel — choose one category and one sub-topic."}
                          </p>
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {showTopicsRequiredHint && selectedSubCount === 0 ? (
                          <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.2 }}
                            className="flex gap-2 rounded-xl border border-amber-200/80 bg-amber-50/95 px-3 py-2 text-amber-950 shadow-sm"
                          >
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
                            <p className="text-[11px] font-semibold leading-snug sm:text-xs">
                              Select one category and one sub-topic to create your account. We opened the
                              browser for you — choose from the list, then save.
                            </p>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>

                      <button
                        type="button"
                        onClick={() => setTopicsPickerOpen(true)}
                        disabled={submitting || catsLoading}
                        className="group relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-xl border border-slate-200/90 bg-white/90 px-3 py-2.5 text-left shadow-sm transition hover:border-[#FFD221]/50 hover:shadow-md active:scale-[0.99] disabled:opacity-50 sm:py-3"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-bold text-slate-900">
                            {catsLoading ? "Loading topic areas…" : "Browse topic areas"}
                          </span>
                          <span className={`mt-0.5 block text-slate-500 ${embed ? "text-[10px] sm:text-xs" : "text-xs"}`}>
                            {selectedSubCount > 0
                              ? `${Object.keys(categoriesPayload)[0]} · ${Object.values(categoriesPayload)[0]?.[0] ?? ""}`
                              : "Tap to open the panel from the right"}
                          </span>
                        </span>
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition group-hover:bg-[#FFD221]/20 group-hover:text-slate-900">
                          <ChevronRight className="h-5 w-5" strokeWidth={2.25} aria-hidden />
                        </span>
                      </button>

                      {selectedSubCount > 0 ? (
                        <div className="space-y-2">
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                            Your selection
                          </p>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {Object.entries(categoriesPayload).map(([cat, subs]) => (
                              <div
                                key={cat}
                                className="rounded-xl border border-slate-200/70 bg-white/95 p-2.5 shadow-sm sm:p-3"
                              >
                                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                  {cat}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {subs.map((sub) => (
                                    <button
                                      key={`${cat}-${sub}`}
                                      type="button"
                                      onClick={() => toggleSubCategory(cat, sub)}
                                      className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/12 px-2.5 py-1 text-left text-[11px] font-semibold text-slate-900 transition hover:bg-[#FFD221]/22"
                                      title={`Remove ${sub}`}
                                    >
                                      <span className="min-w-0 truncate">{sub}</span>
                                      <X className="h-3.5 w-3.5 shrink-0 opacity-60" strokeWidth={2.5} aria-hidden />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className={`rounded-lg bg-slate-100/80 px-2.5 py-1.5 text-slate-500 ${embed ? "text-[10px] sm:text-xs" : "text-xs"}`}>
                          <span className="font-semibold text-slate-700">Tip:</span> pick one topic to start.
                          After you sign in, add more interests anytime from your profile.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            <div
              className={[
                embed ? "col-span-2 rounded-xl border px-3 py-2" : "rounded-2xl border-2 px-4 py-3.5 sm:px-5",
                "transition-all",
                termsAccepted
                  ? "border-[#FFD221]/45 bg-gradient-to-br from-[#FFD221]/[0.07] to-white"
                  : embed
                    ? "border-slate-200 bg-slate-50/90 hover:border-slate-300/90"
                    : "border-slate-200 bg-slate-50/80 hover:border-slate-300/90",
              ].join(" ")}
            >
              <div className={`flex ${embed ? "gap-2" : "gap-3.5"}`}>
                <input
                  id="signup-terms-consent"
                  type="checkbox"
                  className={`mt-0.5 shrink-0 cursor-pointer rounded border-slate-300 accent-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/35 focus:ring-offset-1 ${embed ? "h-4 w-4" : "mt-1 h-[1.125rem] w-[1.125rem]"}`}
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={submitting}
                />
                <label
                  htmlFor="signup-terms-consent"
                  className={
                    embed
                      ? "min-w-0 flex-1 cursor-pointer text-[11px] leading-snug text-slate-700 sm:text-xs"
                      : "min-w-0 flex-1 cursor-pointer text-sm leading-relaxed text-slate-700"
                  }
                >
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#9a7b00] underline decoration-[#FFD221]/70 underline-offset-2 transition hover:text-black"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    Terms &amp; Conditions
                  </a>
                  {embed
                    ? " and to receive updates for my chosen topic."
                    : ", and I want to subscribe to updates and content that match the categories I choose (you can adjust preferences later)."}
                </label>
              </div>
              {!termsAccepted && !embed ? (
                <p className="mt-3 border-t border-slate-200/80 pt-3 pl-0 text-xs text-slate-500 sm:pl-[calc(1.125rem+0.875rem)]">
                  You need to confirm this box before you can create an account.
                </p>
              ) : null}
            </div>

            <AnimatePresence>
              {registrationError ? (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`rounded-lg border border-red-200 bg-red-50 text-red-700 ${embed ? "col-span-2 px-2.5 py-1.5 text-xs" : "rounded-xl px-3 py-2 text-sm"}`}
                >
                  {registrationError}
                </motion.p>
              ) : null}
            </AnimatePresence>

            <div className={`space-y-1.5 ${embed ? "col-span-2" : "pt-1"} w-full`}>
              <motion.button
                whileHover={{ scale: submitting || !termsAccepted ? 1 : 1.01 }}
                whileTap={{ scale: submitting || !termsAccepted ? 1 : 0.99 }}
                type="submit"
                disabled={submitting || !termsAccepted}
                title={!termsAccepted ? "Confirm the agreement above to continue" : undefined}
                className={`flex w-full items-center justify-center gap-2 rounded-xl font-black shadow-[0_12px_32px_-10px_rgba(212,175,55,0.45)] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9a7b00] disabled:cursor-not-allowed disabled:shadow-none ${
                  embed ? "px-4 py-2.5 text-sm" : "px-4 py-3.5 text-sm"
                } ${
                  submitting || !termsAccepted
                    ? "bg-slate-200 text-slate-500"
                    : "bg-[#FFD221] text-black hover:brightness-95"
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 size={embed ? 16 : 18} className="animate-spin" />
                    Creating your account…
                  </>
                ) : (
                  "Create account"
                )}
              </motion.button>
              {!termsAccepted && !submitting && !embed ? (
                <p className="text-center text-[11px] font-medium text-slate-500">
                  Complete the consent above to enable sign up.
                </p>
              ) : null}
            </div>
          </form>
        </div>
  );

  return (
    <>
      {embed ? (
        <div className="space-y-2 sm:space-y-3">
          {signupFormBlock}
          <p className="border-t border-black/[0.06] pt-2 text-center text-xs text-slate-600 sm:pt-3 sm:text-sm">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => onRequestSignIn?.()}
              className="font-semibold text-black hover:text-[#E5B800]"
            >
              Sign in
            </button>
          </p>
        </div>
      ) : (
        <AuthSplitLayout
          whiteLeftPanel
          title="Create your Infoboxx account"
          sideTitle="Join thousands of Ghanaians making informed decisions."
          sideDescription="Real estate, legal, health, nutrition, careers, and betting — Infoboxx has expert guidance for every question."
          sideBullets={[
            "Free account — no credit card required",
            "Personalised content matched to your interests",
            "Direct access to verified expert partners",
          ]}
          footer={
            <p>
              Already have an account?{" "}
              <Link to="/signin" className="font-semibold text-black hover:text-[#E5B800]">
                Sign in
              </Link>
            </p>
          }
        >
          {signupFormBlock}
        </AuthSplitLayout>
      )}

      {typeof document !== "undefined"
        ? createPortal(
            <SignupInterestsDrawer
              open={topicsPickerOpen}
              onClose={() => setTopicsPickerOpen(false)}
              embed={embed}
              catsLoading={catsLoading}
              categories={categories}
              expandedByCategory={expandedByCategory}
              toggleCategoryAccordion={toggleCategoryAccordion}
              expandAllCategoryPanels={expandAllCategoryPanels}
              collapseAllCategoryPanels={collapseAllCategoryPanels}
              interestByCategory={interestByCategory}
              toggleSubCategory={toggleSubCategory}
              categoriesPayload={categoriesPayload}
              selectedSubCount={selectedSubCount}
              submitting={submitting}
            />,
            document.body,
          )
        : null}

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        email={registeredEmail}
        postAuthRedirect={embedPostAuthRedirect ?? location.state?.postAuthRedirect}
        embed={embed}
        onContinueToSignIn={(cleanEmail) =>
          onRequestSignIn?.({
            prefilledEmail: cleanEmail,
            fromRegistration: true,
          })
        }
      />
    </>
  );
};

export default SignUp;